import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../components/store'
import quotes from '../components/quotes.json'
import {
    asBlobURL,
    asDataURL,
    chain,
    getField,
    loadImage,
    pass,
    revokeBlobURL,
    scale,
    SelectedFile,
    swap,
    toDataURL,
    toFile,
} from '../utils/File'

export interface FormState {
    age: null | number
    name: string
    origin: string
    image: null | false | string
    quote: number | 'custom'
    statement: string
    email: string
}
export interface FormProgressState {
    page: number
    downloaded: boolean
    downloadedSecond: boolean
    originalImage: null | false | string
}
const getQuotes = (age: number | null): string | string[] => quotes[age && age >= 18 ? '>=18' : '<18']
const getQuote = (age: number | null, quote: number): string => getQuotes(age)[quote]

const initialState: FormState & FormProgressState = {
    name: '',
    age: null,
    origin: '',
    quote: Math.floor(Math.random() * getQuotes(null).length),
    image: null,
    statement: '',
    email: '',
    page: 0,
    downloaded: false,
    downloadedSecond: false,
    originalImage: null,
}

export const prepareImage = createAsyncThunk('image/prepare', async (imageDataURL: string, { getState }) => {
    return Promise.resolve(imageDataURL)
        .then(toFile('blob'))
        .then(asBlobURL)
        .then(
            chain(
                (a) =>
                    getField<SelectedFile, 'content'>('content')(a)
                        .then(loadImage)
                        .then(
                            scale((img) => Math.min(1, 1080 / Math.max(img.width, img.height))), // Only scale down
                        )
                        .then(toDataURL(a.type))
                        .then(toFile(a.name)),
                swap(pass((a) => getField<SelectedFile, 'content'>('content')(a).then(revokeBlobURL))),
            ),
        )
        .then(asDataURL)
        .then(getField('content'))
        .then((prepared) => ({ prepared, original: imageDataURL }))
})

export const formSlice = createSlice({
    name: 'form',
    initialState,
    reducers: {
        updateFormState: (state, action: PayloadAction<FormState>) => {
            const quotesLength = getQuotes(action.payload.age || state.age).length

            if (state.name !== action.payload.name) {
                state.name = action.payload.name
                state.downloaded = false
            }
            if (state.age !== action.payload.age) {
                state.age = action.payload.age
                state.downloaded = false
            }
            if (state.origin !== action.payload.origin) {
                state.origin = action.payload.origin
                state.downloaded = false
            }
            if (state.email !== action.payload.email) {
                state.email = action.payload.email
                state.downloaded = false
            }

            const originalQuote = typeof action.payload.quote !== 'undefined' ? action.payload.quote : state.quote
            const quote =
                originalQuote === 'custom' || originalQuote < quotesLength
                    ? originalQuote
                    : Math.floor(Math.random() * quotesLength)
            if (state.quote !== quote) {
                state.quote = quote
                state.downloaded = false
            }

            const statement =
                (state.quote !== 'custom' && getQuote(state.age, state.quote)) ||
                action.payload.statement ||
                state.statement
            if (state.statement !== statement) {
                state.statement = statement
                state.downloaded = false
            }
        },
        skipImage: (state) => {
            if (state.image !== false) {
                state.originalImage = false
                state.image = false
                state.downloaded = false
            }
            state.page = Math.min(4, state.page + 1)
        },
        nextPage: (state) => {
            state.page = Math.min(4, state.page + 1)
        },
        previousPage: (state) => {
            state.page = Math.max(0, state.page - 1)
        },
        toPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload
        },
        reset: (state) => {
            state.page = 0
            state.downloadedSecond = false
        },
        download: (state) => {
            state.downloaded = true
        },
        downloadSecond: (state) => {
            state.downloadedSecond = true
        },
    },
    extraReducers: (builder) => {
        // Add reducers for additional action types here, and handle loading state as needed
        builder.addCase(
            prepareImage.fulfilled,
            (state, action: PayloadAction<{ prepared: FormState['image']; original: FormState['image'] }>) => {
                if (state.image !== action.payload.original) {
                    state.originalImage = action.payload.original
                    state.image = action.payload.prepared

                    state.downloaded = false
                }

                state.page = Math.min(4, state.page + 1)
            },
        )
    },
})

// Action creators are generated for each case reducer function
export const { updateFormState, skipImage, nextPage, previousPage, download, downloadSecond, reset, toPage } =
    formSlice.actions

export const selectName = (state: RootState) => state.form.name
export const selectAge = (state: RootState) => state.form.age
export const selectOrigin = (state: RootState) => state.form.origin
export const selectQuote = (state: RootState) => state.form.quote
export const selectImage = (state: RootState) => state.form.image
export const selectForm = (state: RootState) => state.form
export const selectPage = (state: RootState) => state.form.page

export default formSlice.reducer
